import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { FiUser } from 'react-icons/fi';
import { apiGet, apiPut } from '@/lib/api';
import { Drawer } from '@/shared/components/ui/Drawer';
import { Button } from '@/shared/components/ui/Button';
import { Input } from '@/shared/components/ui/Input';
import { Select } from '@/shared/components/ui/Select';
import { FormField } from '@/shared/components/ui/FormField';
import { Skeleton } from '@/shared/components/ui/Skeleton';
import { toast } from 'sonner';

interface UserDetail {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  avatar?: string;
  phone?: string;
  status: string;
  companyId?: string;
  companyName?: string | null;
  lastLogin?: string;
  createdAt: string;
  updatedAt: string;
}

interface UserEditDrawerProps {
  userId: string | null;
  isOpen: boolean;
  onClose: () => void;
}

const STATUS_OPTIONS = [
  { value: 'active', label: 'Ativo' },
  { value: 'inactive', label: 'Inativo' },
];

const formatDate = (dateStr?: string) => {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

export const UserEditDrawer: React.FC<UserEditDrawerProps> = ({ userId, isOpen, onClose }) => {
  const queryClient = useQueryClient();
  const [form, setForm] = useState({ firstName: '', lastName: '', email: '', phone: '', status: 'active' });

  const { data: user, isLoading } = useQuery({
    queryKey: ['admin-user', userId],
    queryFn: () => apiGet<UserDetail>(`/users/${userId}`),
    enabled: !!userId && isOpen,
  });

  useEffect(() => {
    if (user) {
      setForm({
        firstName: user.firstName || '',
        lastName: user.lastName || '',
        email: user.email || '',
        phone: user.phone || '',
        status: user.status || 'active',
      });
    }
  }, [user]);

  const updateMutation = useMutation({
    mutationFn: (data: Record<string, string>) => apiPut(`/users/${userId}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-users'] });
      queryClient.invalidateQueries({ queryKey: ['admin-user', userId] });
      toast.success('Usuario atualizado');
      onClose();
    },
  });

  const handleSubmit = () => {
    if (!form.firstName.trim() || !form.email.trim()) {
      toast.error('Nome e email sao obrigatorios');
      return;
    }
    updateMutation.mutate(form);
  };

  const isSaving = updateMutation.isPending;

  return (
    <Drawer
      isOpen={isOpen}
      onClose={onClose}
      title="Editar Usuario"
      subtitle={user ? `${user.firstName} ${user.lastName}` : undefined}
      icon={<FiUser className="w-5 h-5" />}
      disabled={isSaving}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={isSaving}>Cancelar</Button>
          <Button onClick={handleSubmit} loading={isSaving}>Salvar</Button>
        </>
      }
    >
      {isLoading ? (
        <div className="space-y-4">
          {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} height={40} />)}
        </div>
      ) : (
        <div className="space-y-6">
          {/* User Info */}
          <div>
            <h3 className="text-sm font-semibold mb-3" style={{ color: '#1e293b' }}>Informacoes</h3>
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <FormField label="Nome" required>
                  <Input value={form.firstName} onChange={(e) => setForm(f => ({ ...f, firstName: e.target.value }))} placeholder="Nome" />
                </FormField>
                <FormField label="Sobrenome">
                  <Input value={form.lastName} onChange={(e) => setForm(f => ({ ...f, lastName: e.target.value }))} placeholder="Sobrenome" />
                </FormField>
              </div>
              <FormField label="Email" required>
                <Input type="email" value={form.email} onChange={(e) => setForm(f => ({ ...f, email: e.target.value }))} placeholder="email@exemplo.com" />
              </FormField>
              <FormField label="Telefone">
                <Input value={form.phone} onChange={(e) => setForm(f => ({ ...f, phone: e.target.value }))} placeholder="(00) 00000-0000" />
              </FormField>
              <FormField label="Status">
                <Select value={form.status} onChange={(v) => setForm(f => ({ ...f, status: v }))} options={STATUS_OPTIONS} />
              </FormField>
            </div>
          </div>

          {/* Company info (read-only) */}
          {user?.companyName && (
            <>
              <div style={{ borderTop: '1px solid #e5e7eb' }} />
              <div>
                <h3 className="text-sm font-semibold mb-2" style={{ color: '#1e293b' }}>Company</h3>
                <p className="text-sm" style={{ color: '#334155' }}>{user.companyName}</p>
              </div>
            </>
          )}

          {/* Meta info */}
          {user && (
            <>
              <div style={{ borderTop: '1px solid #e5e7eb' }} />
              <div className="space-y-1 text-xs" style={{ color: '#94a3b8' }}>
                <p>Ultimo login: {formatDate(user.lastLogin)}</p>
                <p>Criado em: {formatDate(user.createdAt)}</p>
                <p>Atualizado em: {formatDate(user.updatedAt)}</p>
              </div>
            </>
          )}
        </div>
      )}
    </Drawer>
  );
};
